import { useMemo } from "react";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import type { Day } from "date-fns";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "@/shared/i18n";

export type DateRangePresetId =
  | "today"
  | "last7Days"
  | "last30Days"
  | "last90Days"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "yearToDate"
  | "lastYear"
  | "allTime";

export interface DateRangePreset {
  id: DateRangePresetId;
  label: string;
  range: () => DateRange;
}

export const DEFAULT_PRESET_IDS: readonly DateRangePresetId[] = [
  "today",
  "last7Days",
  "last30Days",
  "last90Days",
  "thisWeek",
  "lastWeek",
  "thisMonth",
  "lastMonth",
  "yearToDate",
  "lastYear",
  "allTime",
] as const;

// Início do projeto (LikeAPPro foi lançado em 2019). Usado pelo preset
// "allTime"/"Lifetime" como limite inferior. Se o app passar a ter dados
// anteriores, ajustar aqui.
const ALL_TIME_START = new Date(2019, 0, 1);

interface PresetOptions {
  /** Locale para resolver weekStartsOn (segunda em pt/es, domingo em en-US). */
  weekStartsOn?: Day;
  /** Data de referência. Default: agora. */
  reference?: Date;
}

export function getPresetRange(
  id: DateRangePresetId,
  options: PresetOptions = {}
): DateRange {
  const today = options.reference ?? new Date();
  const weekOpts = { weekStartsOn: options.weekStartsOn ?? 1 } as const;
  switch (id) {
    case "today":
      return { from: startOfDay(today), to: endOfDay(today) };
    case "last7Days":
      return { from: startOfDay(subDays(today, 6)), to: endOfDay(today) };
    case "last30Days":
      return { from: startOfDay(subDays(today, 29)), to: endOfDay(today) };
    case "last90Days":
      return { from: startOfDay(subDays(today, 89)), to: endOfDay(today) };
    case "thisWeek":
      return {
        from: startOfDay(startOfWeek(today, weekOpts)),
        to: endOfDay(today),
      };
    case "lastWeek": {
      const lastWeekRef = subWeeks(today, 1);
      return {
        from: startOfDay(startOfWeek(lastWeekRef, weekOpts)),
        to: endOfDay(endOfWeek(lastWeekRef, weekOpts)),
      };
    }
    case "thisMonth":
      return { from: startOfDay(startOfMonth(today)), to: endOfDay(today) };
    case "lastMonth": {
      const lastMonthRef = subMonths(today, 1);
      return {
        from: startOfDay(startOfMonth(lastMonthRef)),
        to: endOfDay(endOfMonth(lastMonthRef)),
      };
    }
    case "yearToDate":
      return { from: startOfDay(startOfYear(today)), to: endOfDay(today) };
    case "lastYear": {
      const lastYearRef = subYears(today, 1);
      return {
        from: startOfDay(startOfYear(lastYearRef)),
        to: endOfDay(endOfYear(lastYearRef)),
      };
    }
    case "allTime":
      return { from: startOfDay(ALL_TIME_START), to: endOfDay(today) };
  }
}

export function isPresetActive(
  preset: DateRangePreset,
  dateRange: DateRange | undefined
): boolean {
  if (!dateRange?.from || !dateRange?.to) return false;
  const range = preset.range();
  if (!range.from || !range.to) return false;
  return isSameDay(dateRange.from, range.from) && isSameDay(dateRange.to, range.to);
}

export function buildDefaultPresets(
  labels: Record<DateRangePresetId, string>,
  options: PresetOptions = {}
): DateRangePreset[] {
  return DEFAULT_PRESET_IDS.map((id) => ({
    id,
    label: labels[id],
    range: () => getPresetRange(id, options),
  }));
}

function resolveWeekStartsOn(language: string): Day {
  // en-US/en (sem locale específico): domingo. Demais (pt, es, en-GB): segunda.
  if (language === "en" || language === "en-US") return 0;
  return 1;
}

/**
 * Hook que retorna os presets padrão já com labels traduzidos via i18n e
 * `weekStartsOn` resolvido pelo idioma atual (en/en-US: domingo; demais: segunda).
 * Memorizado por idioma — recalcula só quando o usuário troca de idioma.
 */
export function useDefaultDatePresets(): DateRangePreset[] {
  const { t, i18n } = useTranslation("date-range-picker");
  return useMemo(
    () =>
      buildDefaultPresets(
        {
          today: t("presets.today"),
          last7Days: t("presets.last7Days"),
          last30Days: t("presets.last30Days"),
          last90Days: t("presets.last90Days"),
          thisWeek: t("presets.thisWeek"),
          lastWeek: t("presets.lastWeek"),
          thisMonth: t("presets.thisMonth"),
          lastMonth: t("presets.lastMonth"),
          yearToDate: t("presets.yearToDate"),
          lastYear: t("presets.lastYear"),
          allTime: t("presets.allTime"),
        },
        { weekStartsOn: resolveWeekStartsOn(i18n.language) }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );
}
